import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getChannel, getStreamUrl, confirmPlayback, addToHistory, addFavorite, removeFavorite, getFavorites, getChannelEPG } from '../services/api';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import Hls from 'hls.js';
import mpegts from 'mpegts.js';
import { 
  ArrowLeft, Heart, Share2, Maximize2, Volume2, VolumeX, 
  Play, Pause, Settings, List, Clock, Tv2
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// ─── Player detection ───────────────────────────────────────────────────────
function detectPlayerType(url, streamType, containerExt) {
  if (!url) return 'hls';
  const inner = (() => { try { return decodeURIComponent(url.toLowerCase()); } catch { return url.toLowerCase(); } })();
  if (inner.includes('.m3u8') || inner.includes('m3u8')) return 'hls';
  if (streamType === 'live') return 'hls';
  // For episodes/vod — use container extension to pick the right player immediately
  const ext = (containerExt || '').toLowerCase();
  if (ext === 'mp4') return 'mp4';
  if (ext === 'ts')  return 'mpegts';
  if (ext === 'm3u8') return 'hls';
  // mkv/avi — try mpegts.js (raw transport stream often mislabelled)
  if (ext === 'mkv' || ext === 'avi') return 'mpegts';
  if (inner.includes('.mp4')) return 'mp4';
  if (inner.includes('.ts'))  return 'mpegts';
  return 'hls';
}

const UA_OPTIONS = [
  { value: 'browser', label: 'Browser', desc: 'Chrome (default)' },
  { value: 'firetv',  label: 'Fire TV',  desc: 'Dalvik/Android 7' },
  { value: 'xtream',  label: 'Xtream',   desc: 'XcodesTVPlayer' },
];

const PLAYER_OPTIONS = [
  { value: 'auto',    label: 'Auto' },
  { value: 'hls',     label: 'HLS' },
  { value: 'mpegts',  label: 'MPEG-TS' },
  { value: 'mp4',     label: 'MP4' },
];

export default function WatchPage() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const hlsRef    = useRef(null);
  const mpegtsRef = useRef(null);
  const heartbeatTimer = useRef(null);
  const containerRef = useRef(null);
  const transcodeForced = useRef(false);

  // Episode / VOD mode: stream URLs passed via route state
  const episodeState = location.state;
  const isEpisode = episodeState?.isEpisode === true;
  const isVod = episodeState?.isVod === true;
  const isFileStream = isEpisode || isVod;
  // Credits are charged against this id (channel for live, stream_id for vod)
  const chargeId = isVod ? episodeState?.chargeId : channelId;

  const [channel, setChannel] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);
  // Episode fallback URLs
  const [streamUrlTs,     setStreamUrlTs]     = useState(null);
  const [streamUrlNative, setStreamUrlNative] = useState(null);
  const [streamType, setStreamType] = useState('live');
  const [epg, setEpg] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [watchTime, setWatchTime] = useState(0);

  // Player + UA settings
  const [playerMode, setPlayerMode]   = useState('auto');
  const [uaMode, setUaMode]           = useState('browser');
  const [showSettings, setShowSettings] = useState(false);
  const [activePlayer, setActivePlayer] = useState(null);
  const [containerExt, setContainerExt] = useState('');

  // Fetch channel + stream URL
  const fetchStreamData = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (isFileStream) {
      setChannel({ name: episodeState.title || (isVod ? 'Movie' : 'Episode'), logo: episodeState.logo || null, group: isVod ? 'Movies' : 'Series' });
      setStreamType(isVod ? 'vod' : 'episode');
      setContainerExt('');
      setStreamUrlTs(null);
      setStreamUrlNative(null);

      // Bail early with a clear message if the browser can't decode H.264/AAC.
      const canH264 = (window.MediaSource &&
          (MediaSource.isTypeSupported('video/mp4; codecs="avc1.42e01e"') ||
           MediaSource.isTypeSupported('video/mp4; codecs="avc1.640028"')))
        || document.createElement('video').canPlayType('video/mp4; codecs="avc1.42E01E"') !== '';
      if (!canH264) {
        setError('This browser cannot decode H.264 video. Please use Chrome, Edge, Safari, or Firefox.');
        setLoading(false);
        return;
      }

      try {
        const startUrl = episodeState.streamUrl; // /api/stream/transcode?...
        const abs = startUrl.startsWith('http') ? startUrl : `${BACKEND_URL}${startUrl}`;
        const res = await axios.get(abs, { timeout: 60000 });
        setStreamUrl(`${BACKEND_URL}${res.data.playlist}`);
      } catch (e) {
        setError('Could not start this stream. The source may be offline or temporarily unavailable.');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const [channelData, streamData, epgData, favoritesData] = await Promise.all([
        getChannel(channelId),
        getStreamUrl(channelId, uaMode),
        getChannelEPG(channelId).catch(() => ({ programs: [] })),
        getFavorites()
      ]);
      setChannel(channelData);
      setStreamUrl(streamData.stream_url);
      setStreamType(streamData.stream_type || 'live');
      setEpg(epgData.programs || []);
      setIsFavorite(favoritesData.some(f => f.id === channelId));
      addToHistory(channelId).catch(console.error);
    } catch (err) {
      console.error('Error loading channel:', err);
      setError(err.response?.data?.detail || 'Failed to load channel');
      toast.error(err.response?.data?.detail || 'Failed to load channel');
    } finally {
      setLoading(false);
    }
  }, [channelId, uaMode, isEpisode, isVod]);

  useEffect(() => { fetchStreamData(); }, [fetchStreamData]);

  // Initialize player — smart detection + fallback
  useEffect(() => {
    if (!streamUrl || !videoRef.current) return;

    const video = videoRef.current;
    let mounted = true;

    // ── Destroy previous players ───────────────────────────────────────────
    const destroyAll = () => {
      if (heartbeatTimer.current) { clearTimeout(heartbeatTimer.current); heartbeatTimer.current = null; }
      if (hlsRef.current)    { hlsRef.current.destroy();    hlsRef.current    = null; }
      if (mpegtsRef.current) { mpegtsRef.current.destroy(); mpegtsRef.current = null; }
      video.pause();
      video.removeAttribute('src');
      video.load();
    };
    destroyAll();

    // Schedule heartbeat: deduct credit only after 10s of real playback.
    // Episodes are never charged; live + VOD charge against chargeId.
    const scheduleHeartbeat = () => {
      if (isEpisode || !chargeId) return;
      if (heartbeatTimer.current) clearTimeout(heartbeatTimer.current);
      heartbeatTimer.current = setTimeout(() => {
        confirmPlayback(chargeId);
      }, 10000);
    };

    const resolved = playerMode === 'auto' ? detectPlayerType(streamUrl, streamType, containerExt) : playerMode;
    setActivePlayer(resolved);

    // ── Helper: try MPEG-TS via mpegts.js ─────────────────────────────────
    const tryMpegTs = (url) => {
      if (!url) { if (mounted) tryNative(streamUrlNative); return; }
      if (!mpegts.isSupported()) { if (mounted) tryNative(url); return; }
      const player = mpegts.createPlayer({
        type: 'mpegts',
        url,
        isLive: streamType === 'live',
        enableWorker: true,
      });
      player.attachMediaElement(video);
      player.load();
      player.on(mpegts.Events.ERROR, (type, detail) => {
        console.warn('mpegts error:', type, detail);
        player.destroy();
        mpegtsRef.current = null;
        if (mounted) tryNative(streamUrlNative);
      });
      const p = player.play();
      if (p !== undefined) p.catch(e => { if (e.name !== 'AbortError' && mounted) tryNative(streamUrlNative); });
      mpegtsRef.current = player;
      setActivePlayer('mpegts');
    };

    // ── Helper: try native HTML5 video ────────────────────────────────────
    const tryNative = (url) => {
      if (!url) {
        if (mounted) setError('Stream unavailable — no compatible format found for this content.');
        return;
      }
      video.src = url;
      const p = video.play();
      if (p !== undefined) {
        p.then(() => scheduleHeartbeat())
         .catch(e => {
          if (e.name === 'AbortError' || e.name === 'NotAllowedError') return;
          if (mounted) setError('Stream unavailable. Try a different Client Profile in the player settings (⚙).');
        });
      } else {
        scheduleHeartbeat();
      }
      setActivePlayer('native');
    };

    // ── Main player init ───────────────────────────────────────────────────
    if (resolved === 'hls') {
      if (Hls.isSupported()) {
        const isFile = streamType === 'vod' || streamType === 'episode';
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 90,
          maxBufferLength: 60,
          startPosition: isFile ? 0 : -1,
        });
        hls.loadSource(streamUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (!mounted) return;
          const p = video.play();
          if (p !== undefined) {
            p.then(() => scheduleHeartbeat())
             .catch(e => { if (e.name !== 'AbortError' && e.name !== 'NotAllowedError') console.warn(e); });
          } else {
            scheduleHeartbeat();
          }
        });
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (!data.fatal) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) { hls.startLoad(); return; }
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR)   { hls.recoverMediaError(); return; }
          // Fatal — for episodes, cascade to MPEG-TS then native
          hls.destroy(); hlsRef.current = null;
          if (!mounted) return;
          if (streamType === 'episode' || streamType === 'vod') {
            // Copied stream won't decode (e.g. HEVC source) — retry with a full H.264 transcode.
            if (!transcodeForced.current && episodeState?.streamUrl) {
              transcodeForced.current = true;
              toast.info('Optimizing video for your browser…');
              const startUrl = episodeState.streamUrl;
              const abs = (startUrl.startsWith('http') ? startUrl : `${BACKEND_URL}${startUrl}`) + '&force=1';
              axios.get(abs, { timeout: 60000 })
                .then(r => { if (mounted) setStreamUrl(`${BACKEND_URL}${r.data.playlist}`); })
                .catch(() => { if (mounted) setError('This title could not be played.'); });
            } else {
              setError('Stream unavailable. Please try another title.');
            }
          } else {
            setError('Stream unavailable. Try a different Client Profile in settings.');
          }
        });
        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari — native HLS
        video.src = streamUrl;
        video.play().catch(e => { if (e.name !== 'AbortError' && mounted) setError('Playback failed.'); });
      } else {
        // No HLS support → skip to MPEG-TS
        if (streamType === 'episode' || streamType === 'vod') tryMpegTs(streamUrlTs);
        else setError('HLS not supported by this browser.');
      }
    } else if (resolved === 'mpegts') {
      tryMpegTs(streamUrl);
    } else {
      // mp4 / direct native
      tryNative(streamUrl);
    }

    return () => {
      mounted = false;
      destroyAll();
    };
  }, [streamUrl, streamUrlTs, streamUrlNative, playerMode, streamType, containerExt, channelId, isEpisode]);

  // Track watch time
  useEffect(() => {
    const interval = setInterval(() => {
      if (isPlaying) {
        setWatchTime(prev => prev + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying]);

  // Save watch time on unmount
  useEffect(() => {
    return () => {
      if (watchTime > 0 && channelId) {
        addToHistory(channelId, watchTime).catch(console.error);
      }
    };
  }, [channelId, watchTime]);

  // Auto-hide controls
  useEffect(() => {
    let timeout;
    
    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 3000);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => {
        container.removeEventListener('mousemove', handleMouseMove);
        clearTimeout(timeout);
      };
    }
  }, []);

  const handleFavoriteToggle = async () => {
    try {
      if (isFavorite) {
        await removeFavorite(channelId);
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await addFavorite(channelId);
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        containerRef.current.requestFullscreen();
      }
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center" data-testid="watch-loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[rgb(124,58,237)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">{isFileStream ? 'Preparing your stream… this can take up to 20 seconds' : 'Loading stream...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center" data-testid="watch-error">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <Tv2 className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Stream Unavailable</h2>
          <p className="text-white/60 mb-6">{error}</p>
          <Button onClick={() => navigate(-1)} variant="outline" className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="watch-page">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Back to Channels</span>
      </button>

      {/* Video Player */}
      <div 
        ref={containerRef}
        className="player-container relative aspect-video bg-black rounded-2xl overflow-hidden"
        data-testid="video-player"
      >
        <video
          ref={videoRef}
          className="w-full h-full"
          playsInline
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />

        {/* Player Controls Overlay */}
        <div 
          className={`player-controls absolute inset-0 flex flex-col justify-between p-6 bg-gradient-to-t from-black/80 via-transparent to-black/40 transition-opacity duration-300 ${
            showControls ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Top Bar */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{channel?.name}</h2>
              <p className="text-white/60">{channel?.group}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFavoriteToggle}
                className={`rounded-full ${isFavorite ? 'text-red-500' : 'text-white/60 hover:text-white'}`}
                data-testid="favorite-toggle"
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleShare}
                className="rounded-full text-white/60 hover:text-white"
                data-testid="share-button"
              >
                <Share2 className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Center Play Button */}
          <div className="flex-1 flex items-center justify-center">
            <button
              onClick={togglePlay}
              className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
              data-testid="play-pause-btn"
            >
              {isPlaying ? (
                <Pause className="w-10 h-10 text-white" />
              ) : (
                <Play className="w-10 h-10 text-white ml-1" />
              )}
            </button>
          </div>

          {/* Bottom Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleMute}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="mute-toggle"
              >
                {isMuted ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>
              <div className="flex items-center gap-2 text-sm text-white/60">
                <Clock className="w-4 h-4" />
                <span>{formatTime(watchTime)}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Player Settings Toggle */}
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowSettings(s => !s); }}
                  className={`p-2 rounded-lg transition-colors flex items-center gap-1 text-xs font-semibold ${showSettings ? 'bg-white/20 text-white' : 'hover:bg-white/10 text-white/60'}`}
                  data-testid="player-settings-btn"
                >
                  <Settings className="w-4 h-4" />
                  {activePlayer && <span className="uppercase">{activePlayer}</span>}
                </button>

                {showSettings && (
                  <div
                    className="absolute bottom-10 right-0 bg-black/90 border border-white/10 rounded-xl p-3 w-64 backdrop-blur-sm z-50"
                    data-testid="player-settings-panel"
                    onClick={e => e.stopPropagation()}
                  >
                    {/* Player Mode */}
                    <div className="mb-3">
                      <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Player</p>
                      <div className="flex gap-1 flex-wrap">
                        {PLAYER_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => setPlayerMode(opt.value)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${playerMode === opt.value ? 'bg-[rgb(124,58,237)] text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
                            data-testid={`player-mode-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* User-Agent */}
                    <div>
                      <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Client Profile</p>
                      <div className="flex flex-col gap-1">
                        {UA_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => { setUaMode(opt.value); setShowSettings(false); }}
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs transition-all ${uaMode === opt.value ? 'bg-[rgb(124,58,237)] text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
                            data-testid={`ua-mode-${opt.value}`}
                          >
                            <span className="font-semibold">{opt.label}</span>
                            <span className="opacity-60">{opt.desc}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={toggleFullscreen}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="fullscreen-toggle"
              >
                <Maximize2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Channel Info & EPG */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Channel Details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-start gap-4">
              {channel?.logo ? (
                <img 
                  src={channel.logo} 
                  alt={channel.name}
                  className="w-16 h-16 rounded-xl object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-[rgb(31,31,34)] flex items-center justify-center">
                  <Tv2 className="w-8 h-8 text-white/30" />
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-xl font-bold">{channel?.name}</h3>
                <p className="text-white/60">{channel?.group}</p>
              </div>
              <Button
                onClick={handleFavoriteToggle}
                variant={isFavorite ? 'default' : 'outline'}
                className={`rounded-full ${isFavorite ? 'bg-red-500 hover:bg-red-600' : ''}`}
              >
                <Heart className={`w-4 h-4 mr-2 ${isFavorite ? 'fill-current' : ''}`} />
                {isFavorite ? 'Favorited' : 'Add to Favorites'}
              </Button>
            </div>
          </div>
        </div>

        {/* EPG Sidebar */}
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <List className="w-5 h-5 text-[rgb(124,58,237)]" />
            <h4 className="font-semibold">Program Guide</h4>
          </div>
          
          {epg.length > 0 ? (
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {epg.slice(0, 10).map((program, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg ${index === 0 ? 'bg-[rgb(124,58,237)]/20 border border-[rgb(124,58,237)]/30' : 'bg-white/5'}`}
                >
                  <p className="font-medium text-sm">{program.title}</p>
                  <p className="text-xs text-white/40 mt-1">
                    {formatEPGTime(program.start)} - {formatEPGTime(program.stop)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/40 text-sm">No program guide available</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper functions
function formatTime(seconds) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatEPGTime(timeStr) {
  if (!timeStr) return '';
  try {
    // EPG times are usually in format: YYYYMMDDHHmmss
    const hour = timeStr.substring(8, 10);
    const minute = timeStr.substring(10, 12);
    return `${hour}:${minute}`;
  } catch {
    return timeStr;
  }
}
